import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Layers, Globe, Puzzle, ArrowRight, CheckCircle2 } from "lucide-react";

const ICONS = { layers: Layers, globe: Globe, puzzle: Puzzle };
const COLOR_MAP = {
  sky: { bg: "bg-sky-50", icon: "text-sky-600", check: "text-sky-500" },
  indigo: { bg: "bg-indigo-50", icon: "text-indigo-600", check: "text-indigo-500" },
  emerald: { bg: "bg-emerald-50", icon: "text-emerald-600", check: "text-emerald-500" },
};

export const PRODUCTS_DEFAULT_DATA = {
  hero: {
    title_prefix: "Our",
    title_accent: "Products",
    tagline: "A comprehensive suite designed to manage every aspect of your promotional products and print business.",
  },
  products: [
    {
      id: "advance", icon: "layers", title: "Advance", href: "/advance", color: "sky",
      tagline: "Brings manufacturers, suppliers, decorators, distributors, service providers, and clients onto one connected platform.",
      description: "Advance is the core of the Antera ecosystem. It unifies all aspects of your promotional products business into a single, powerful platform that eliminates data silos and streamlines every workflow.",
      features: ["Unified order management system","Customer relationship management (CRM)","Production workflow automation","Financial management and reporting","Multi-location support","Role-based access control","Custom dashboards and analytics","Document management"],
      image: "/api/media/acd5f68d-154c-447a-9ae5-087a270de539",
    },
    {
      id: "aether", icon: "globe", title: "Aether", href: "/aether", color: "indigo",
      tagline: "Powers customizable storefronts that blend modern design with deep back-office integration.",
      description: "Aether enables you to create beautiful, branded online storefronts that are deeply integrated with your back-office systems. Sell products, manage inventory, and process orders seamlessly.",
      features: ["Customizable storefront templates","Product catalog management","Real-time inventory sync","Shopping cart and checkout","Customer account management","Mobile-responsive design","SEO optimization tools","Payment gateway integration"],
      image: "/api/media/d3793caf-5a31-47b3-b3ea-a283f5106340",
    },
    {
      id: "integrations", icon: "puzzle", title: "Integrations", href: "/integrations", color: "emerald",
      tagline: "Connections and workflows that deliver powerful solutions across your systems and teams.",
      description: "Connect Antera with the tools you already use. Our integrations ecosystem ensures data flows smoothly between all your systems, eliminating manual data entry and reducing errors.",
      features: ["QuickBooks Online & Desktop","PromoStandards data feeds","Shipping carrier APIs","Payment processor connections","Email marketing integrations","Third-party marketplace sync","Custom API access","Webhook support"],
      image: "/api/media/acd5f68d-154c-447a-9ae5-087a270de539",
    },
  ],
  bottom_cta: {
    heading: "See our products in action",
    subheading: "Get a guided walkthrough of Advance, Aether, and our integration ecosystem tailored to your business needs.",
    primary_label: "Request a Demo",
    primary_href: "/contact",
    secondary_label: "Contact Sales",
    secondary_href: "/contact",
  },
};

export default function ProductsPage({ data }) {
  const d = { ...PRODUCTS_DEFAULT_DATA, ...(data || {}) };
  const hero = { ...PRODUCTS_DEFAULT_DATA.hero, ...(d.hero || {}) };
  const bottom = { ...PRODUCTS_DEFAULT_DATA.bottom_cta, ...(d.bottom_cta || {}) };
  const items = d.products || [];

  return (
    <div data-testid="products-page" className="page-transition">
      <section className="pt-32 pb-14 lg:pt-40 lg:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
              {hero.title_prefix} <span className="text-sky-600">{hero.title_accent}</span>
            </h1>
            <p className="mt-5 text-base lg:text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto">{hero.tagline}</p>
          </motion.div>
        </div>
      </section>

      {items.map((p, i) => {
        const c = COLOR_MAP[p.color] || COLOR_MAP.sky;
        const Icon = ICONS[p.icon] || Layers;
        return (
          <section key={p.id || i} id={p.id} data-testid={`product-section-${p.id}`} className={`py-16 lg:py-24 ${i % 2 === 0 ? "bg-slate-50/50" : "bg-white"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className={`w-12 h-12 rounded-xl ${c.bg} flex items-center justify-center mb-5`}>
                    <Icon className={`h-6 w-6 ${c.icon}`} />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{p.title}</h2>
                  <p className="mt-2 text-base font-medium text-slate-600">{p.tagline}</p>
                  <p className="mt-4 text-sm text-slate-500 leading-relaxed">{p.description}</p>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <CheckCircle2 className={`h-4 w-4 ${c.check} shrink-0 mt-0.5`} />
                        <span className="text-sm text-slate-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <Link to="/contact">
                      <Button data-testid={`product-${p.id}-demo-btn`} className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 text-sm font-semibold">
                        Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    {p.href && (
                      <Link to={p.href}>
                        <Button variant="outline" data-testid={`product-${p.id}-learn-more-btn`} className="border-sky-200 text-sky-700 hover:bg-sky-50 hover:text-sky-700 rounded-full px-6 text-sm font-semibold">
                          Learn More <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>

                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  {p.image ? (
                    <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200">
                      <img src={p.image} alt={p.title} className="w-full h-auto" />
                    </div>
                  ) : (
                    <div className="bg-slate-100 rounded-xl h-64 lg:h-80 flex items-center justify-center">
                      <Icon className="h-20 w-20 text-slate-300" />
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </section>
        );
      })}

      <section className="bg-slate-900 py-14 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">{bottom.heading}</h2>
          <p className="mt-3 text-sm text-slate-400 max-w-lg mx-auto">{bottom.subheading}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={bottom.primary_href || "/contact"}>
              <Button data-testid="products-bottom-demo-btn" className="bg-sky-500 hover:bg-sky-400 text-white rounded-full px-8 h-12 text-sm font-semibold">
                {bottom.primary_label} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            {bottom.secondary_label ? (
              <Link to={bottom.secondary_href || "/contact"}>
                <Button variant="outline" data-testid="products-bottom-contact-btn" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-full px-8 h-12 text-sm font-semibold">
                  {bottom.secondary_label}
                </Button>
              </Link>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
